<script setup lang="ts">
import { PropType } from "vue";
import Menubar from "primevue/menubar";
import type { MenuItem } from "primevue/menuitem";
import SkipLink from "./SkipLink.vue";

// Shared top-header chrome: the sticky menubar with a per-app nav model
// (:items), the common active-menuitem nav-link rendering, and #start / #end
// slots for each app's logo + controls (UserMenu, LanguageSelection, …).
// `.sticky-menu` / `.active-menuitem` / `.app-wrapper` are styled by each app's
// global CSS (so apps keep their own header look); only the menubar's own
// .p-menubar tweak lives here. Pass skipLinkTarget to render a SkipLink.
defineProps({
    items: { type: Array as PropType<MenuItem[]>, default: () => [] },
    highlight: { type: String, default: "" },
    skipLinkTarget: { type: String, default: "" },
});
</script>

<template>
    <!-- No padding utilities here: PrimeVue's .p-menubar supplies the padding.
         The old px-4/py-2 were inert (PrimeVue beat them on source order), and
         keeping them post-migration made them live again -- py-2 additionally
         matched an app rule `.py-2,.py-5,.p-4{padding:1rem!important}` meant for
         PrimeFlex, which doubled the header padding and squeezed the mobile row. -->
    <div class="p-menubar sticky-menu">
        <SkipLink v-if="skipLinkTarget" :target="skipLinkTarget" />
        <Menubar
            :model="items"
            orientation="horizontal"
            class="app-wrapper"
            id="main-menu"
            tabindex="-1"
            :pt="{
                end: { class: 'justify-between w-full md:w-min' },
                root: { class: 'pl-0 md:pl-3' },
            }"
        >
            <template #start>
                <slot name="start" />
            </template>
            <template #item="{ label, item, props }">
                <a
                    :href="item.route || item.url"
                    :target="item.target || '_self'"
                    v-bind="props.action"
                    :class="[{ 'active-menuitem': highlight === item.id }]"
                    :aria-current="highlight === item.id ? 'page' : undefined"
                >
                    <span v-bind="props.icon" />
                    <span v-bind="props.label">{{ label }}</span>
                </a>
            </template>
            <template #end>
                <slot name="end" />
            </template>
        </Menubar>
    </div>
</template>

<style scoped>
.p-menubar {
    border: 0;
    padding-left: 1em;
    background-color: var(--p-menubar-background);
}
</style>
